import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Wifi, KeyRound, MapPin, Phone, MessageCircle, Mail, Copy, Home, ExternalLink, Info, DoorOpen, ConciergeBell, CheckCircle2, Loader2, Train, ScrollText, Route, Recycle, Compass, LogOut, X, ChevronRight, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SECTION_ICONS = { checkin: KeyRound, rules: ScrollText, howto: Route, waste: Recycle, tips: Compass, checkout: LogOut, custom: Info };

const UI = {
  it: { chiama: "Chiama", portone: "Portone", whatsapp: "WhatsApp", email: "Email", mappa: "Mappa",
    wifi: "Wi-Fi", network: "Rete", password: "Password", checkin: "Check-in & accesso", keybox: "Codice keybox",
    gateTitle: "Apertura automatica portone", gateText: "Premi il pulsante per chiamare e aprire automaticamente il portone principale.",
    gateBtn: "Chiama ora", gateNote: "Chiamata gratuita dal tuo telefono. Nessun WhatsApp, nessun operatore.", gateTime: "Tempo apertura", secUnit: "secondi",
    gate2Title: "Apertura portone interno", gate2Text: "Premi il pulsante per chiamare e aprire il portone interno.", gate2Btn: "Apri Portone Interno", portoneInt: "Interno",
    smartOpen: "Apri con", smartGeneric: "Apri con accesso smart", doorOpen: "Apri porta appartamento", lockCode: "Codice serratura", discover: "Scopri gli appartamenti di Urban Dreams Home di Olena & Dante",
    servicesTitle: "Servizi extra su richiesta", servicesSub: "Seleziona un servizio e inviaci la richiesta: ti risponderemo con una proposta su misura per le tue esigenze.",
    request: "Richiedi", requested: "Richiesto",
    lgTitle: "Zona, mappa & dintorni", transports: "Trasporti", atmMap: "Mappa ATM Milano", touristBus: "Bus turistico", tickets: "Biglietti", hostTips: "Consigli dell'host", openMaps: "Apri in Google Maps →",
    contactsTitle: "Contatti rapidi", fullGuide: "Apri la Welcome Guide completa",
    footer: "Powered by Urban OS · Urban Dreams Home",
    modalSub: "Inviaci la richiesta: valuteremo la disponibilità e ti risponderemo con una proposta dedicata. Nessun addebito ora.",
    guests: "Numero di persone", notes: "Note (facoltative)", notesPh: "Orari, preferenze, dettagli…", namePh: "Il tuo nome", contactPh: "Telefono / email", submit: "Invia richiesta",
    loading: "Caricamento…", errTitle: "Contenuto non disponibile", errSub: "Contatta la struttura per assistenza.",
    bReq: "Richiesta inviata. Ti invieremo un preventivo personalizzato al più presto.", bPaid: "Pagamento completato! Il servizio è stato confermato. Grazie.",
    bPend: "Pagamento in elaborazione. Riceverai conferma a breve.", bCancel: "Pagamento annullato. Puoi riprovare quando vuoi.", bErr: "Si è verificato un errore. Riprova.",
    lists: { parking: "Parcheggi", restaurants: "Ristoranti consigliati", supermarkets: "Supermercati", bike: "Bike / Monopattini", attractions: "Attrazioni", airports: "Aeroporti", emergency: "Numeri utili & emergenze" } },
  en: { chiama: "Call", portone: "Gate", whatsapp: "WhatsApp", email: "Email", mappa: "Map",
    wifi: "Wi-Fi", network: "Network", password: "Password", checkin: "Check-in & access", keybox: "Keybox code",
    gateTitle: "Automatic main door opening", gateText: "Press the button to call and automatically open the main door.",
    gateBtn: "Call now", gateNote: "Free call from your phone. No WhatsApp, no operator.", gateTime: "Opening time", secUnit: "seconds",
    gate2Title: "Internal gate opening", gate2Text: "Press the button to call and open the internal gate.", gate2Btn: "Open Internal Gate", portoneInt: "Internal",
    smartOpen: "Open with", smartGeneric: "Open with smart access", doorOpen: "Open apartment door", lockCode: "Lock code", discover: "Discover Urban Dreams Home apartments by Olena & Dante",
    servicesTitle: "Extra services on request", servicesSub: "Select a service and send us a request: we'll reply with a tailored proposal for your needs.",
    request: "Request", requested: "Requested",
    lgTitle: "Area, map & surroundings", transports: "Transport", atmMap: "Milan ATM map", touristBus: "Tourist bus", tickets: "Tickets", hostTips: "Host tips", openMaps: "Open in Google Maps →",
    contactsTitle: "Quick contacts", fullGuide: "Open the full Welcome Guide",
    footer: "Powered by Urban OS · Urban Dreams Home",
    modalSub: "Send us your request: we'll check availability and reply with a dedicated proposal. No charge now.",
    guests: "Number of guests", notes: "Notes (optional)", notesPh: "Times, preferences, details…", namePh: "Your name", contactPh: "Phone / email", submit: "Send request",
    loading: "Loading…", errTitle: "Content unavailable", errSub: "Please contact the property for assistance.",
    bReq: "Request sent. We'll send you a personalized quote as soon as possible.", bPaid: "Payment completed! The service has been confirmed. Thank you.",
    bPend: "Payment processing. You'll receive confirmation shortly.", bCancel: "Payment cancelled. You can try again anytime.", bErr: "An error occurred. Please try again.",
    lists: { parking: "Parking", restaurants: "Recommended restaurants", supermarkets: "Supermarkets", bike: "Bike / Scooters", attractions: "Attractions", airports: "Airports", emergency: "Useful & emergency numbers" } },
  es: { chiama: "Llamar", portone: "Portón", whatsapp: "WhatsApp", email: "Email", mappa: "Mapa",
    wifi: "Wi-Fi", network: "Red", password: "Contraseña", checkin: "Check-in y acceso", keybox: "Código keybox",
    gateTitle: "Apertura automática de la puerta principal", gateText: "Pulsa el botón para llamar y abrir automáticamente la puerta principal.",
    gateBtn: "Llamar ahora", gateNote: "Llamada gratuita desde tu teléfono. Sin WhatsApp, sin operador.", gateTime: "Tiempo de apertura", secUnit: "segundos",
    gate2Title: "Apertura portón interior", gate2Text: "Pulsa el botón para llamar y abrir el portón interior.", gate2Btn: "Abrir Portón Interior", portoneInt: "Interior",
    smartOpen: "Abrir con", smartGeneric: "Abrir con acceso inteligente", doorOpen: "Abrir puerta del apartamento", lockCode: "Código de cerradura", discover: "Descubre los apartamentos de Urban Dreams Home de Olena & Dante",
    servicesTitle: "Servicios extra a petición", servicesSub: "Selecciona un servicio y envíanos tu solicitud: te responderemos con una propuesta a medida.",
    request: "Solicitar", requested: "Solicitado",
    lgTitle: "Zona, mapa y alrededores", transports: "Transporte", atmMap: "Mapa ATM Milán", touristBus: "Bus turístico", tickets: "Entradas", hostTips: "Consejos del anfitrión", openMaps: "Abrir en Google Maps →",
    contactsTitle: "Contactos rápidos", fullGuide: "Abrir la Welcome Guide completa",
    footer: "Powered by Urban OS · Urban Dreams Home",
    modalSub: "Envíanos tu solicitud: comprobaremos la disponibilidad y te responderemos con una propuesta dedicada. Sin cargo ahora.",
    guests: "Número de personas", notes: "Notas (opcionales)", notesPh: "Horarios, preferencias, detalles…", namePh: "Tu nombre", contactPh: "Teléfono / email", submit: "Enviar solicitud",
    loading: "Cargando…", errTitle: "Contenido no disponible", errSub: "Contacta con el alojamiento para asistencia.",
    bReq: "Solicitud enviada. Te enviaremos un presupuesto personalizado lo antes posible.", bPaid: "¡Pago completado! El servicio ha sido confirmado. Gracias.",
    bPend: "Pago en proceso. Recibirás confirmación en breve.", bCancel: "Pago cancelado. Puedes intentarlo cuando quieras.", bErr: "Se ha producido un error. Inténtalo de nuevo.",
    lists: { parking: "Aparcamientos", restaurants: "Restaurantes recomendados", supermarkets: "Supermercados", bike: "Bicis / Patinetes", attractions: "Atracciones", airports: "Aeropuertos", emergency: "Números útiles y emergencias" } },
};

const fmtTime = (v, unit) => { const m = String(v || "").match(/\d+/); return m ? `${m[0]} ${unit}` : v; };

const detectLang = () => {
  const q = new URLSearchParams(window.location.search).get("lang");
  if (q && UI[q]) return q;
  const saved = localStorage.getItem("udh_lang");
  if (saved && UI[saved]) return saved;
  const b = (navigator.language || "it").slice(0, 2).toLowerCase();
  return UI[b] ? b : "it";
};

const Section = ({ icon: Icon, title, children, accent = "#0f172a", testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
    <div className="mb-3 flex items-center gap-2 text-sm font-semibold" style={{ color: accent }}>
      <Icon size={18} /> {title}
    </div>
    {children}
  </div>
);

const LG_LISTS = [
  { key: "parking", fields: ["name", "address", "distance", "cost"], link: "maps_link" },
  { key: "restaurants", fields: ["name", "category", "distance", "price"], link: "maps_link" },
  { key: "supermarkets", fields: ["name", "address", "distance", "hours"], link: "maps_link" },
  { key: "bike", fields: ["provider", "nearest", "notes"], link: "link" },
  { key: "attractions", fields: ["name", "distance"], link: "maps_link" },
  { key: "airports", fields: ["name", "how_to", "time", "cost"], link: "maps_link" },
  { key: "emergency", fields: ["label", "phone", "note"] },
];

function LocalGuideBlock({ lg, t, tr }) {
  const trans = lg.transport || {};
  const hasTransport = trans.metro || trans.tram || trans.bus || trans.walk_distance;
  const anyList = LG_LISTS.some((l) => (lg[l.key] || []).length > 0);
  const tb = lg.tourist_bus || {};
  if (!lg.atm_map_url && !hasTransport && !anyList && !(tb.operator || tb.stop) && !(lg.host_tips || []).length) return null;
  return (
    <Section icon={MapPin} title={t.lgTitle} accent="#38bdf8" testid="guest-localguide">
      <div className="space-y-4">
        {(lg.atm_map_url || hasTransport) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400"><Train size={13} /> {t.transports}</div>
            {lg.atm_map_url && <a href={lg.atm_map_url} target="_blank" rel="noreferrer" data-testid="guest-atm-map" className="inline-flex items-center gap-1.5 rounded-lg bg-sky-500/15 px-3 py-2 text-sm text-sky-200"><Train size={14} /> {t.atmMap}</a>}
            {hasTransport && (
              <div className="text-sm text-slate-300">
                {trans.metro && <div>🚇 {trans.metro}</div>}
                {trans.tram && <div>🚋 {trans.tram}</div>}
                {trans.bus && <div>🚌 {trans.bus}</div>}
                {trans.maps_link && <a href={trans.maps_link} target="_blank" rel="noreferrer" className="text-xs text-sky-300">{t.openMaps}</a>}
              </div>
            )}
          </div>
        )}
        {LG_LISTS.map((L) => (lg[L.key] || []).length > 0 && (
          <div key={L.key} className="space-y-1.5" data-testid={`guest-lg-${L.key}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t.lists[L.key]}</div>
            {lg[L.key].map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-2 rounded-lg bg-white/[0.03] px-3 py-2 text-sm">
                <div className="min-w-0"><div className="font-medium text-slate-200">{it[L.fields[0]]}</div><div className="text-xs text-slate-400">{L.fields.slice(1).map((f) => it[f]).filter(Boolean).join(" · ")}</div></div>
                {L.link && it[L.link] && <a href={it[L.link]} target="_blank" rel="noreferrer" className="shrink-0 text-sky-300"><ExternalLink size={14} /></a>}
              </div>
            ))}
          </div>
        ))}
        {(lg.host_tips || []).length > 0 && (
          <div className="space-y-1.5" data-testid="guest-lg-host_tips">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">💡 {t.hostTips}</div>
            {lg.host_tips.map((h, i) => <div key={i} className="rounded-lg bg-white/[0.03] px-3 py-2 text-sm"><div className="font-medium text-slate-200">{tr(`tip_title:${i}`, h.title)}</div><div className="text-xs text-slate-400">{tr(`tip_text:${i}`, h.text)}</div></div>)}
          </div>
        )}
      </div>
    </Section>
  );
}

function QuickPill({ href, icon: Icon, label, testid, accent }) {
  return (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}
      className="flex min-w-[68px] flex-1 flex-col items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.06] px-2 py-3 text-[11px] font-medium text-slate-200 transition-colors hover:bg-white/[0.12]">
      <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: accent + "22", color: accent }}><Icon size={17} /></span>{label}
    </a>
  );
}

function WelcomeBlock({ block, lang }) {
  const pick = (base, i18n) => (i18n && i18n[lang]) || (lang !== "it" && i18n && i18n.en) || base || "";
  return (
    <div data-testid={`guest-wc-${block.key}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-200">
        <span className="text-lg">{block.emoji}</span> {block.label}
      </div>
      <div className="space-y-3">
        {block.items.map((it) => {
          const title = pick(it.title, it.title_i18n);
          const body = pick(it.body, it.body_i18n);
          const fileHref = `${BACKEND}${it.url}`;
          if (it.type === "text") return (
            <div key={it.id} data-testid={`guest-wc-item-${it.id}`}>
              {title && <div className="text-sm font-medium text-slate-100">{title}</div>}
              {body && <p className="whitespace-pre-line text-sm leading-relaxed text-slate-300">{body}</p>}
            </div>
          );
          if (it.type === "image") return (
            <figure key={it.id} data-testid={`guest-wc-item-${it.id}`} className="space-y-1">
              <img src={fileHref} alt={title} className="w-full rounded-xl object-cover" />
              {title && <figcaption className="text-xs text-slate-400">{title}</figcaption>}
            </figure>
          );
          if (it.type === "video") return (
            <div key={it.id} data-testid={`guest-wc-item-${it.id}`} className="space-y-1">
              {title && <div className="text-sm font-medium text-slate-100">{title}</div>}
              <video controls playsInline preload="metadata" src={fileHref} className="w-full rounded-xl bg-black" />
            </div>
          );
          if (it.type === "pdf") return (
            <a key={it.id} data-testid={`guest-wc-item-${it.id}`} href={fileHref} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-3 text-sm text-slate-200 transition-colors hover:bg-white/[0.1]">
              <ScrollText size={16} className="shrink-0 text-amber-300" />
              <span className="min-w-0 flex-1 truncate">{title || it.file_name || "Documento PDF"}</span>
              <ExternalLink size={14} className="shrink-0 text-slate-400" />
            </a>
          );
          if (it.type === "link") return (
            <a key={it.id} data-testid={`guest-wc-item-${it.id}`} href={it.url} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-3 text-sm text-slate-200 transition-colors hover:bg-white/[0.1]">
              <ExternalLink size={16} className="shrink-0 text-amber-300" />
              <span className="min-w-0 flex-1 truncate">{title || it.url}</span>
            </a>
          );
          return null;
        })}
      </div>
    </div>
  );
}

export default function GuestGuide() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(detectLang());
  const [payBanner, setPayBanner] = useState(null);
  const [reqItem, setReqItem] = useState(null);
  const [form, setForm] = useState({ guests: 2, note: "", guest_name: "", guest_contact: "" });
  const [submitting, setSubmitting] = useState(false);
  const [requested, setRequested] = useState({});

  const t = UI[lang] || UI.en;
  const changeLang = (l) => { setLang(l); localStorage.setItem("udh_lang", l); };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/guide/${code}`);
        if (r.data.type === "url" && r.data.redirect) { window.location.href = r.data.redirect; return; }
        setData(r.data);
      } catch (e) { setErr(e?.response?.data?.detail || "Content unavailable"); }
      finally { setLoading(false); }
    })();
  }, [code]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const st = p.get("upsell");
    if (!st) return;
    if (st === "cancel") { setPayBanner("cancel"); return; }
    const sid = p.get("session_id");
    if (st === "success" && sid) {
      axios.get(`${API}/public/upsell/status/${sid}`).then((r) => setPayBanner(r.data.payment_status === "paid" ? "paid" : "pending")).catch(() => setPayBanner("pending"));
    } else if (st === "success") setPayBanner("paid");
    window.history.replaceState({}, "", `/g/${code}`);
  }, [code]);

  const trackGate = (method) => { try { axios.post(`${API}/public/gate-click/${code}`, { method }); } catch (_) {} };
  const trackDiscover = () => { try { axios.post(`${API}/public/discover-click/${code}`, { lang }); } catch (_) {} };
  const openRequest = (item) => { setReqItem(item); setForm({ guests: data?.apartment?.max_guests || 2, note: "", guest_name: "", guest_contact: "" }); };
  const submitRequest = async () => {
    if (!reqItem) return;
    try {
      setSubmitting(true);
      await axios.post(`${API}/public/service-request/${code}`, { item_key: reqItem.key, guests: Number(form.guests) || 1, note: form.note, guest_name: form.guest_name, guest_contact: form.guest_contact, lang });
      setRequested((r) => ({ ...r, [reqItem.key]: true }));
      setReqItem(null); setPayBanner("requested");
    } catch (e) { setPayBanner("error"); }
    finally { setSubmitting(false); }
  };
  const copy = (txt) => { navigator.clipboard?.writeText(txt); };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400" data-testid="guest-loading">{t.loading}</div>;
  if (err || !data) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-300" data-testid="guest-error">
      <Info size={40} className="text-slate-500" /><p className="text-lg font-medium">{t.errTitle}</p><p className="text-sm text-slate-500">{t.errSub}</p>
    </div>
  );

  const { apartment, cover_image, wifi, checkin, sections, contacts, welcome_guide_url, auto_gate, auto_gate2, smart_access, door_lock, services, local_guide, translations, welcome_center, discover_url } = data;
  const tr = (id, base) => (translations?.[lang]?.[id]) || (lang !== "it" ? translations?.en?.[id] : "") || base;
  const svcLabel = (it) => tr(`service:${it.key}`, lang === "en" ? (it.label_en || it.label) : lang === "es" ? (it.label_es || it.label) : it.label);
  const heroTitle = tr("field:hero_title", data.hero_title || apartment.name);
  const heroSub = tr("field:hero_subtitle", data.hero_subtitle || apartment.zone);
  const intro = tr("field:intro", data.intro);
  const waNum = contacts?.whatsapp ? contacts.whatsapp.replace(/[^0-9]/g, "") : "";
  const mapsHref = local_guide?.transport?.maps_link || (apartment?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(apartment.address)}` : "");
  const banners = { requested: ["border-emerald-400/30 bg-emerald-500/10 text-emerald-200", t.bReq], paid: ["border-emerald-400/30 bg-emerald-500/10 text-emerald-200", t.bPaid], pending: ["border-amber-400/30 bg-amber-500/10 text-amber-200", t.bPend], cancel: ["border-slate-500/30 bg-white/5 text-slate-300", t.bCancel], error: ["border-red-400/30 bg-red-500/10 text-red-200", t.bErr] };
  // Welcome Guide (📘) e Video (🎥) vengono mostrati SUBITO dopo il Check-in (dove servono all'ospite),
  // il resto delle sezioni Welcome Center resta più in basso. Layout invariato.
  const HOIST = ["welcome_guide", "access_video"];
  const wc = welcome_center || [];
  const wcTop = HOIST.map((k) => wc.find((b) => b.key === k)).filter(Boolean);
  const wcRest = wc.filter((b) => !HOIST.includes(b.key));
  const hasHoistedGuide = wcTop.some((b) => b.key === "welcome_guide");

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="guest-guide">
      <div className="relative h-72 w-full overflow-hidden" data-testid="guest-hero">
        {cover_image ? <img src={cover_image} alt={heroTitle} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-indigo-700 to-slate-900" />}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10" />
        <div className="absolute right-4 top-4 flex gap-1 rounded-full border border-white/15 bg-slate-950/50 p-1 backdrop-blur" data-testid="guest-lang-switch">
          {["it", "en", "es"].map((l) => (
            <button key={l} onClick={() => changeLang(l)} data-testid={`lang-${l}`}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase transition-colors ${lang === l ? "bg-white text-slate-900" : "text-slate-200 hover:bg-white/10"}`}>{l}</button>
          ))}
        </div>
        <div className="absolute bottom-5 left-0 w-full px-6">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-indigo-300"><Home size={14} /> Urban Dreams Home</div>
          <h1 className="mt-1.5 font-head text-3xl font-semibold leading-tight">{heroTitle}</h1>
          {heroSub && <p className="mt-1 text-sm text-slate-300">{heroSub}</p>}
        </div>
      </div>

      <div className="mx-auto max-w-lg space-y-4 px-4 py-6">
        <div className="flex gap-2" data-testid="guest-quickbar">
          {auto_gate?.phone && <QuickPill href={`tel:${auto_gate.phone}`} icon={DoorOpen} label={t.portone} testid="quick-gate" accent="#34d399" />}
          {auto_gate2?.phone && <QuickPill href={`tel:${auto_gate2.phone}`} icon={DoorOpen} label={t.portoneInt} testid="quick-gate2" accent="#22d3ee" />}
          {contacts?.phone && <QuickPill href={`tel:${contacts.phone}`} icon={Phone} label={t.chiama} testid="quick-call" accent="#fbbf24" />}
          {waNum && <QuickPill href={`https://wa.me/${waNum}`} icon={MessageCircle} label={t.whatsapp} testid="quick-whatsapp" accent="#34d399" />}
          {contacts?.email && <QuickPill href={`mailto:${contacts.email}`} icon={Mail} label={t.email} testid="quick-email" accent="#818cf8" />}
          {mapsHref && <QuickPill href={mapsHref} icon={MapPin} label={t.mappa} testid="quick-maps" accent="#38bdf8" />}
        </div>

        {payBanner && banners[payBanner] && (
          <div data-testid="guest-pay-banner" className={`flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm ${banners[payBanner][0]}`}><CheckCircle2 size={18} className="shrink-0" /> {banners[payBanner][1]}</div>
        )}
        {intro && <p className="text-sm leading-relaxed text-slate-300" data-testid="guest-intro">{intro}</p>}

        {data.gallery?.length > 0 && (
          <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-1" data-testid="guest-gallery">
            {data.gallery.map((g) => <img key={g.id} src={g.url} alt={g.caption || heroTitle} className="h-40 w-64 shrink-0 snap-center rounded-2xl object-cover" />)}
          </div>
        )}

        {(checkin?.address || checkin?.keybox_code || auto_gate?.phone || auto_gate2?.phone || smart_access || door_lock) && (
          <Section icon={KeyRound} title={t.checkin} accent="#34d399" testid="guest-checkin">
            {(checkin?.address || checkin?.keybox_code) && (
              <div className="mb-4 space-y-2 text-sm">
                {checkin.address && <div className="flex items-start gap-2 text-slate-300"><MapPin size={15} className="mt-0.5 shrink-0 text-slate-400" /> {checkin.address}</div>}
                {checkin.keybox_code && <div className="flex items-center justify-between"><span className="text-slate-400">{t.keybox}</span><span className="rounded-lg bg-white/10 px-2.5 py-1 font-mono">{checkin.keybox_code}</span></div>}
              </div>
            )}
            {auto_gate?.phone && (
              <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5" data-testid="guest-autogate">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-300"><DoorOpen size={18} /> {t.gateTitle}</div>
                <p className="mb-3 text-sm leading-relaxed text-emerald-100/90">{t.gateText}</p>
                {auto_gate.time && <p className="mb-3 text-xs text-emerald-200/70">{t.gateTime}: {fmtTime(auto_gate.time, t.secUnit)}</p>}
                <a href={`tel:${auto_gate.phone}`} onClick={() => trackGate("phone")} data-testid="guest-autogate-call"
                  className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500 py-4 text-base font-semibold text-white shadow-lg shadow-emerald-500/25 transition-transform active:scale-[0.98]"><Phone size={19} /> {t.gateBtn}</a>
                <p className="mt-2 text-center text-[11px] text-emerald-200/60">{t.gateNote}</p>
              </div>
            )}
            {auto_gate2?.phone && (
              <div className={`rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-5 ${auto_gate?.phone ? "mt-3" : ""}`} data-testid="guest-autogate2">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-300"><DoorOpen size={18} /> {t.gate2Title}</div>
                <p className="mb-3 text-sm leading-relaxed text-cyan-100/90">{t.gate2Text}</p>
                {auto_gate2.time && <p className="mb-3 text-xs text-cyan-200/70">{t.gateTime}: {fmtTime(auto_gate2.time, t.secUnit)}</p>}
                <a href={`tel:${auto_gate2.phone}`} onClick={() => trackGate("phone")} data-testid="guest-autogate2-call"
                  className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 py-4 text-base font-semibold text-white shadow-lg shadow-cyan-500/25 transition-transform active:scale-[0.98]"><Phone size={19} /> {t.gate2Btn}</a>
                <p className="mt-2 text-center text-[11px] text-cyan-200/60">{t.gateNote}</p>
              </div>
            )}
            {smart_access && (
              <a href={smart_access.url} target="_blank" rel="noreferrer" onClick={() => trackGate("smart")} data-testid="guest-smartaccess-open"
                className={`flex items-center justify-center gap-2 rounded-xl border border-sky-400/40 bg-sky-500/15 py-3.5 text-sm font-semibold text-sky-200 transition-transform active:scale-[0.98] ${auto_gate?.phone ? "mt-3" : ""}`}>
                <KeyRound size={17} /> {smart_access.provider ? `${t.smartOpen} ${smart_access.provider.charAt(0).toUpperCase() + smart_access.provider.slice(1)}` : t.smartGeneric}
              </a>
            )}
            {door_lock?.url && (
              <a href={door_lock.url} target="_blank" rel="noreferrer" onClick={() => trackGate("door")} data-testid="guest-doorlock-open"
                className={`flex items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/15 py-3.5 text-sm font-semibold text-emerald-200 transition-transform active:scale-[0.98] ${(auto_gate?.phone || smart_access) ? "mt-3" : ""}`}>
                <DoorOpen size={17} /> {door_lock.provider ? `${t.doorOpen} · ${door_lock.provider.charAt(0).toUpperCase() + door_lock.provider.slice(1)}` : t.doorOpen}
              </a>
            )}
            {door_lock?.code && (
              <div className={`flex items-center justify-between text-sm ${(auto_gate?.phone || smart_access || door_lock?.url) ? "mt-3" : ""}`} data-testid="guest-doorlock-code">
                <span className="text-slate-400">{t.lockCode}</span>
                <button onClick={() => copy(door_lock.code)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 font-mono text-xs">{door_lock.code} <Copy size={13} /></button>
              </div>
            )}
          </Section>
        )}

        {wcTop.map((b) => <WelcomeBlock key={b.key} block={b} lang={lang} />)}
        {!hasHoistedGuide && welcome_guide_url && (
          <a href={welcome_guide_url} target="_blank" rel="noreferrer" data-testid="guest-full-guide-top"
            className="flex items-center justify-center gap-2 rounded-2xl border border-amber-400/30 bg-amber-500/10 py-3 text-sm font-medium text-amber-200">
            📘 {t.fullGuide} <ExternalLink size={15} />
          </a>
        )}

        {discover_url && (
          <a href={discover_url} target="_blank" rel="noreferrer" onClick={trackDiscover} data-testid="guest-discover-cta"
            className="group flex w-full items-center gap-3 rounded-2xl bg-slate-100 px-5 py-4 font-bold text-slate-900 shadow-sm transition-colors hover:bg-white">
            <Home size={20} className="shrink-0 text-slate-700" />
            <span className="flex-1 text-sm leading-snug sm:text-base">{t.discover}</span>
            <ExternalLink size={16} className="shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
          </a>
        )}

        {wifi && (
          <Section icon={Wifi} title={t.wifi} accent="#818cf8">
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="text-slate-400">{t.network}</span><span className="font-medium">{wifi.name}</span></div>
              {wifi.password && <div className="flex items-center justify-between"><span className="text-slate-400">{t.password}</span><button data-testid="guest-wifi-copy" onClick={() => copy(wifi.password)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 font-mono text-xs">{wifi.password} <Copy size={13} /></button></div>}
            </div>
          </Section>
        )}

        {sections?.map((s) => {
          const Icon = SECTION_ICONS[s.key] || Info;
          const body = tr(`section_body:${s.id}`, s.body);
          return (
            <Section key={s.id} icon={Icon} title={tr(`section_title:${s.id}`, s.title)} accent="#f472b6" testid={`guest-section-${s.key}`}>
              <p className="whitespace-pre-line text-sm leading-relaxed text-slate-300">{body}</p>
            </Section>
          );
        })}

        {wcRest.map((b) => <WelcomeBlock key={b.key} block={b} lang={lang} />)}

        {services?.length > 0 && (
          <Section icon={ConciergeBell} title={t.servicesTitle} accent="#a78bfa" testid="guest-services">
            <p className="mb-3 text-xs text-slate-400">{t.servicesSub}</p>
            <div className="space-y-2">
              {services.map((it) => (
                <button key={it.key} data-testid={`service-${it.key}`} onClick={() => openRequest(it)} disabled={!!requested[it.key]}
                  className="flex w-full items-center justify-between gap-3 rounded-xl bg-white/[0.05] p-3.5 text-left transition-colors hover:bg-white/[0.1] disabled:opacity-60">
                  <span className="text-sm font-medium">{svcLabel(it)}</span>
                  {requested[it.key] ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-300"><CheckCircle2 size={14} /> {t.requested}</span> : <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-300">{t.request} <ChevronRight size={14} /></span>}
                </button>
              ))}
            </div>
          </Section>
        )}

        {local_guide && <LocalGuideBlock lg={local_guide} t={t} tr={tr} />}

        {contacts && (contacts.phone || contacts.whatsapp || contacts.email) && (
          <Section icon={Phone} title={t.contactsTitle} accent="#fbbf24" testid="guest-contacts">
            <div className="flex flex-wrap gap-2">
              {contacts.phone && <a href={`tel:${contacts.phone}`} data-testid="guest-call" className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2 text-sm"><Phone size={15} /> {t.chiama}</a>}
              {waNum && <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" data-testid="guest-whatsapp" className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500/15 px-3 py-2 text-sm text-emerald-300"><MessageCircle size={15} /> {t.whatsapp}</a>}
              {contacts.email && <a href={`mailto:${contacts.email}`} data-testid="guest-email" className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2 text-sm"><Mail size={15} /> {t.email}</a>}
            </div>
            {contacts.name && <p className="mt-2 text-xs text-slate-500">{contacts.name}</p>}
          </Section>
        )}

        <p className="pt-4 text-center text-[11px] text-slate-600">{t.footer}</p>
      </div>

      {reqItem && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 backdrop-blur-sm sm:items-center" data-testid="service-request-modal">
          <div className="w-full max-w-lg rounded-t-3xl border border-white/10 bg-slate-900 p-5 sm:rounded-3xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-base font-semibold text-slate-100"><ConciergeBell size={18} className="text-violet-300" /> {svcLabel(reqItem)}</div>
              <button onClick={() => setReqItem(null)} data-testid="service-request-close" className="text-slate-400 hover:text-slate-200"><X size={20} /></button>
            </div>
            <p className="mb-4 text-xs text-slate-400">{t.modalSub}</p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">{t.guests}</label>
                <input type="number" min="1" value={form.guests} data-testid="service-request-guests" onChange={(e) => setForm((f) => ({ ...f, guests: e.target.value }))} className="w-full rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm outline-none focus:border-violet-400/50" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-400">{t.notes}</label>
                <textarea rows={3} value={form.note} data-testid="service-request-note" onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder={t.notesPh} className="w-full rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm outline-none focus:border-violet-400/50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.guest_name} data-testid="service-request-name" onChange={(e) => setForm((f) => ({ ...f, guest_name: e.target.value }))} placeholder={t.namePh} className="rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm outline-none focus:border-violet-400/50" />
                <input value={form.guest_contact} data-testid="service-request-contact" onChange={(e) => setForm((f) => ({ ...f, guest_contact: e.target.value }))} placeholder={t.contactPh} className="rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-sm outline-none focus:border-violet-400/50" />
              </div>
            </div>
            <button onClick={submitRequest} disabled={submitting} data-testid="service-request-submit" className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-violet-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/20 transition-transform active:scale-[0.98] disabled:opacity-60">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t.submit}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
